import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { InputWork, InputWorkTitle } from '../../shared/component/headless/input/InputBody';
import { DimensionsType, DisplayAlignment, DisplaySize, ImageStyle, PEInfoType, TextAlignment, usesWorkDuration } from '../../shared/enum/EnumRepository';
import { convertWorkSizeToString, PEWorkData, SizeData } from '../../shared/dto/EntityRepository';
import { useValidation } from '../../shared/hooks/useValidation';
import { Info, VariableSizeLabel } from './WorkInfo';
import SizeInputForPE from './SizeInputForPE';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import MoleculeTextAreaDiv from '../../shared/component/molecule/MoleculeTextAreaDiv';
import { InputWorkTextArea } from '../../shared/component/headless/textarea/TextAreaBody';
import { useEditMode } from '../../shared/hooks/useEditMode';
import MobileBanWrapper from '../../shared/component/MobileBanWrapper';
import { TitleOblique } from './WorkInfo';
import { useSettingStore } from '../../shared/store/settingStore';
import { resolveWorkArtistName, shouldShowWorkArtist } from '../../shared/util/workArtist';

export interface WorkInfoProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  imageStyle: ImageStyle;
  infoType: PEInfoType;
  data: PEWorkData;
}

const WorkInfoLeftComp: React.FC<WorkInfoProps> = ({ peId, alignment, displaySize, imageStyle, infoType, data }) => {
  const handleChange = useProjectElementListStore((s) => s.updateWork);
  const { checkType } = useValidation();
  const { isEditMode } = useEditMode();
  const setting = useSettingStore((state) => state.setting);
  const artistName = !isEditMode && shouldShowWorkArtist(setting)
    ? resolveWorkArtistName(data.artist)
    : '';

  const handleChangeWithValidate = (field: keyof PEWorkData, value: string | SizeData | DimensionsType) => {
    if (!checkType(field, value)) {
      return;
    };
    handleChange(peId, { [field]: value });
  }

  const defaultAlignment = alignment || DisplayAlignment.CENTER;
  const defaultDisplaySize = displaySize || DisplaySize.BIG;
  const defaultImageStyle = imageStyle || ImageStyle.LANDSCAPE;

  const inlineInfo = useMemo(() => {
    const sizeOrDuration =
      usesWorkDuration(data.workType)
        ? (data.duration || "—")
        : convertWorkSizeToString(data.size, data.dimensionsType, data.sizeText);
    return data.prodYear + ", " + data.material + ", " + sizeOrDuration;
  }, [data.prodYear, data.material, data.size, data.sizeText, data.dimensionsType, data.workType, data.duration]);
  //useMemo transition 영향 없음
  const leftAligned = useMemo(
    () => isLeftAligned(defaultAlignment, defaultDisplaySize, defaultImageStyle, infoType),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle, infoType]
  );

  const leftAlignedOnly = useMemo(
    () => isLeftAlignedOnly(defaultAlignment, defaultDisplaySize, defaultImageStyle),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle]
  );

  const flexGrow = useMemo(
    () => getFlexGrow(defaultAlignment, defaultDisplaySize, defaultImageStyle),
    [defaultAlignment, defaultDisplaySize, defaultImageStyle]
  );

  const renderInlineInfo = () => {
    if (isEditMode) {
      return (
        <>
          <MoleculeInputDiv
            value={data.prodYear}
            placeholder={"Year"}
            handleChange={(e) => handleChangeWithValidate("prodYear", e.target.value)}
            inputStyle={InfoWorkInputRightOnly}
            StyledDiv={Info}
          />
          <MoleculeTextAreaDiv
            value={data.material}
            placeholder={"Material"}
            handleChange={(e) => handleChangeWithValidate("material", e.target.value)}
            textareaStyle={InputWorkTextArea}
            StyledDiv={Info}
            alignment={TextAlignment.CENTER}
          />
          {usesWorkDuration(data.workType) ? (
            <MoleculeInputDiv
              value={data.duration}
              placeholder={"00:00:00 (hh:mm:ss)"}
              handleChange={(e) => handleChangeWithValidate("duration", e.target.value)}
              inputStyle={InfoWorkInputRightOnly}
              StyledDiv={Info}
            />
          ) : (
            <>
              {data.dimensionsType === DimensionsType.FIXED && (
                <SizeInputForPE
                  value={data.size}
                  onChange={(next) => handleChangeWithValidate("size", next)}
                  StyledDiv={Info}
                />
              )}
              {data.dimensionsType === DimensionsType.CUSTOM && (
                <MoleculeInputDiv
                  value={data.sizeText ?? ''}
                  placeholder={"Custom size"}
                  handleChange={(e) => handleChangeWithValidate("sizeText", e.target.value)}
                  inputStyle={InfoWorkInputRightOnly}
                  StyledDiv={Info}
                />
              )}
              {data.dimensionsType === DimensionsType.VARIABLE && (
                <VariableSizeLabel>Dimensions variable</VariableSizeLabel>
              )}
            </>
          )}
        </>
      )
    } else {
      return (
        <>
          {inlineInfo}
        </>
      )
    }
  }


  return (
    <MobileBanWrapper>
      <ImgContentInfoWrapperLeft $flexGrow={flexGrow} $leftAligned={leftAligned} $leftAlignedOnly={leftAlignedOnly}>
        {infoType !== PEInfoType.NONE && (
          <InfoWrapperLeft
            $leftAlignedOnly={leftAlignedOnly}
            $displaySize={defaultDisplaySize}
            $imageStyle={defaultImageStyle}
          >
            <InlineInfo $leftAlignedOnly={leftAlignedOnly}>
              {artistName && <span>{artistName}, </span>}<TitleOblique>{data.title}, </TitleOblique>{inlineInfo}
            </InlineInfo>
            {isEditMode &&
              <>
                <MoleculeInputDiv
                  value={data.title}
                  placeholder={""}
                  handleChange={(e) => handleChangeWithValidate("title", e.target.value)}
                  inputStyle={InputWorkTitle}
                  StyledDiv={TitleLeft}
                />
                <WorkInfo $isEditMode={isEditMode}>
                  {renderInlineInfo()}
                </WorkInfo>
              </>}
          </InfoWrapperLeft>
        )}
      </ImgContentInfoWrapperLeft>
    </MobileBanWrapper>
  );
};

export const getFlexGrow = (
  $displayAlignment: DisplayAlignment,
  $displaySize?: DisplaySize,
  $imageStyle?: ImageStyle
): string => {
  if ($displaySize === DisplaySize.WIDE && $imageStyle === ImageStyle.LANDSCAPE) {
    return '0';
  }
  switch ($displayAlignment) {
    case DisplayAlignment.LEFT_FLUSH:     // ┏img──────┛
      return '0';
    case DisplayAlignment.LEFT_OFFSET:    // ┏──¼│img───┛
      return '2';
    case DisplayAlignment.RIGHT_OFFSET:   // ┏───img│¼──┛
      return '6';
    case DisplayAlignment.RIGHT_FLUSH:    // ┏──────img┛
      return '1';
    case DisplayAlignment.CENTER:         // ┏──½│img│½──┛
    default:
      return '1';
  }
};

/**
 * 이미지가 왼쪽 정렬 되어있으면 (LEFT_FLUSH, LEFT_OFFSET)  
 * 이미지가 중앙 정렬 되어있으면 (CENTER)  
 * 보이지 않는다.
 */
export const isLeftAligned = (
  $displayAlignment: DisplayAlignment,
  $displaySize?: DisplaySize,
  $imageStyle?: ImageStyle,
  $infoType: PEInfoType = PEInfoType.INLINE,
): boolean => {
  if ($infoType === PEInfoType.NONE) return true;
  if ($displaySize === DisplaySize.WIDE && $imageStyle === ImageStyle.LANDSCAPE) {
    return true;
  }
  return [
    DisplayAlignment.LEFT_FLUSH,
    DisplayAlignment.LEFT_OFFSET,
    DisplayAlignment.CENTER
  ].includes($displayAlignment);
};

/**
 * 이미지가 왼쪽 정렬 되어있으면 (LEFT_FLUSH, LEFT_OFFSET)  
 * 이미지가 중앙 정렬 되어있으면 (CENTER)  
 * WIDE + LANDSCAPE이면 alignment와 무관하게 CENTER와 같다.  
 * 보이지 않는다.
 */
export const isLeftAlignedOnly = (
  $displayAlignment: DisplayAlignment,
  $displaySize?: DisplaySize,
  $imageStyle?: ImageStyle,
): boolean => {
  if ($displaySize === DisplaySize.WIDE && $imageStyle === ImageStyle.LANDSCAPE) {
    return true;
  }
  return [
    DisplayAlignment.LEFT_FLUSH,
    DisplayAlignment.LEFT_OFFSET,
    DisplayAlignment.CENTER
  ].includes($displayAlignment);
};

export const ImgContentInfoWrapperLeft = styled.div<{ $flexGrow: string, $leftAligned: boolean, $leftAlignedOnly: boolean }>`
  display: flex;
  flex: ${({ $flexGrow }) => `${$flexGrow} 1 0%`};
  min-width: 0;
  justify-content: flex-end;
  opacity: ${({ $leftAligned }) => $leftAligned ? 0 : 1};
  transform: ${({ $leftAlignedOnly }) => $leftAlignedOnly ? 'translateX(15vw)' : 'translateX(0vw)'};
  z-index: ${({ $leftAligned }) => $leftAligned ? -10 : 1};
  pointer-events: ${({ $leftAligned }) => $leftAligned ? 'none' : 'auto'};
  transition: flex-grow 0.7s ease, opacity 0.5s ease, transform 0.6s ease;
`;

export const InfoWrapperLeft = styled.div<{
  $leftAlignedOnly: boolean,
  $displaySize: DisplaySize,
  $imageStyle: ImageStyle,
}>`
  width: ${({ $leftAlignedOnly, $displaySize, $imageStyle }) => {
    if ($leftAlignedOnly) {
      return '0vw';
    }
    if ($displaySize === DisplaySize.WIDE && $imageStyle === ImageStyle.LANDSCAPE) {
      return '0vw';
    }
    return '98%';
  }};
  max-width: 19.5vw;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  // overflow: hidden;
`;

export const TitleLeft = styled.h2`
  width: 100%;
  margin-bottom: 6px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-align: right;
  font-style: oblique;
  ${({ theme }) => theme.typography.Body_02_2};
`;

const WorkInfo = styled.div<{ $isEditMode: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: ${({ $isEditMode }) => $isEditMode ? 'column' : 'row'};
  justify-content: flex-end;
  align-items: center;
  gap: 1px;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-align: right;
`;

const InfoWorkInputRightOnly = styled(InputWork)`
  width: 100%;
`;

const InlineInfo = styled.div<{ $leftAlignedOnly: boolean }>`
  display: ${({ $leftAlignedOnly }) => ($leftAlignedOnly ? 'none' : 'block')};
  width: 100%;
  min-width: 0;
  text-align: right;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

export default WorkInfoLeftComp;
